import React from 'react';
import { Eye, Pencil, UserX, UserCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isPatientUser } from '../utils/userProfileForm';

function Avatar({ user }) {
  const initial = (user.name || user.email || '?')[0].toUpperCase();
  return user.photo_url ? (
    <img src={user.photo_url} alt="" className="h-12 w-12 rounded-full object-cover border border-[#E8EEF5]" />
  ) : (
    <div className="h-12 w-12 rounded-full bg-[#E8EEF5] grid place-items-center text-sm font-bold text-[#071B34]">
      {initial}
    </div>
  );
}

/** Users directory table — deactivate/reactivate instead of delete. */
export default function UsersTable({
  users, roles, specs, loading, roleLabel, deactivated = false,
  onChangeRole, onChangeSpec, onView, onEdit, onDeactivate, onReactivate,
}) {
  return (
    <div className="bg-white border rounded-xl overflow-hidden" data-testid="ta-users-table">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b text-left text-zinc-600">
          <tr>
            <th className="px-4 py-3 font-medium">Doctor / staff</th>
            <th className="px-4 py-3 font-medium">Hospital role</th>
            <th className="px-4 py-3 font-medium">Specialisation</th>
            <th className="px-4 py-3 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={4} className="px-4 py-10 text-center text-zinc-500">
                <Loader2 className="h-5 w-5 animate-spin inline" />
              </td>
            </tr>
          ) : users.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-4 py-10 text-center text-zinc-500">
                {deactivated ? 'No deactivated users.' : 'No active users.'}
              </td>
            </tr>
          ) : users.map((u) => (
            <tr key={u.id} className="border-b last:border-0 align-top" data-testid={`ta-user-row-${u.email}`}>
              <td className="px-4 py-3">
                <div className="flex items-start gap-3 min-w-[220px]">
                  <Avatar user={u} />
                  <div className="min-w-0">
                    <p className="font-semibold text-zinc-900 truncate">{u.name || '—'}</p>
                    {u.title ? <p className="text-[11px] text-zinc-500 truncate">{u.title}</p> : null}
                    <p className="text-xs text-zinc-600 truncate">{u.email}</p>
                    {u.qualifications ? (
                      <p className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2">{u.qualifications}</p>
                    ) : null}
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                <select
                  className="h-8 border rounded-md px-2 text-xs max-w-[200px]"
                  value={u.clinic_role_key || ''}
                  onChange={(e) => onChangeRole(u, e.target.value)}
                  disabled={deactivated}
                  data-testid={`ta-user-role-${u.email}`}
                >
                  {!roles.find((r) => r.role_key === u.clinic_role_key) && (
                    <option value={u.clinic_role_key || ''}>{roleLabel(u)}</option>
                  )}
                  {roles.map((r) => (
                    <option key={r.role_key} value={r.role_key}>{r.name}</option>
                  ))}
                </select>
              </td>
              <td className="px-4 py-3">
                <select
                  className="h-8 border rounded-md px-2 text-xs max-w-[180px]"
                  value={u.specialization || ''}
                  onChange={(e) => onChangeSpec(u, e.target.value)}
                  disabled={deactivated}
                >
                  <option value="">—</option>
                  {u.specialization && !specs.find((s) => s.name === u.specialization) && (
                    <option value={u.specialization}>{u.specialization}</option>
                  )}
                  {specs.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              </td>
              <td className="px-4 py-3 text-right whitespace-nowrap space-x-1.5">
                {!isPatientUser(u) && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => onView(u)}
                      data-testid={`ta-user-view-${u.email}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                    {!deactivated && (
                      <Button variant="outline" size="sm" onClick={() => onEdit(u)}
                        data-testid={`ta-user-edit-${u.email}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                    )}
                  </>
                )}
                {deactivated ? (
                  <Button variant="outline" size="sm" onClick={() => onReactivate(u)}
                    className="text-emerald-700 border-emerald-200"
                    data-testid={`ta-user-reactivate-${u.email}`}>
                    <UserCheck className="h-3.5 w-3.5 mr-1" /> Reactivate
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => onDeactivate(u)}
                    className="text-amber-800 border-amber-200"
                    data-testid={`ta-user-deactivate-${u.email}`}>
                    <UserX className="h-3.5 w-3.5 mr-1" /> Deactivate
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
